import React from 'react';
import './PrivacyPolicy.css';

/**
 * Privacy Policy Component
 * 
 * Displays the privacy policy for StoicCredit application.
 * Covers data collection, usage, third-party services, and user rights.
 */
const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy-container">
      <div className="privacy-policy-content">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        
        <section className="privacy-section">
          <h2>1. Introduction</h2>
          <p>
            Welcome to StoicCredit ("we," "our," or "us"). We are committed to protecting your privacy and ensuring you have a positive experience on our platform. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our business credit guide application.
          </p>
          <p>
            By using our service, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our service.
          </p>
        </section>

        <section className="privacy-section">
          <h2>2. Information We Collect</h2>
          
          <h3>2.1 Information You Provide</h3>
          <ul>
            <li><strong>Account Information:</strong> When you create an account, we collect your email address, display name, and authentication credentials through Firebase Authentication.</li>
            <li><strong>Assessment Data:</strong> We collect information you provide in credit assessment forms, including financial goals, credit objectives, timelines, and related financial information.</li>
            <li><strong>Chat History:</strong> We store conversations you have with our AI assistant, including questions, responses, and context related to your credit planning.</li>
            <li><strong>Subscription Information:</strong> When you subscribe to our service, we collect payment information through Stripe, including billing details and subscription preferences.</li>
          </ul>

          <h3>2.2 Automatically Collected Information</h3>
          <ul>
            <li><strong>Usage Analytics:</strong> We use Firebase Analytics to track how you interact with our platform, including page views, feature usage, button clicks, and navigation patterns.</li>
            <li><strong>Device Information:</strong> We may collect information about your device, browser type, operating system, and IP address.</li>
            <li><strong>Cookies and Tracking Technologies:</strong> We use cookies and similar tracking technologies to enhance your experience and analyze usage patterns.</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>3. How We Use Your Information</h2>
          <p>We use the information we collect for the following purposes:</p>
          <ul>
            <li><strong>Service Delivery:</strong> To provide, maintain, and improve our credit planning services, including generating personalized credit plans and recommendations.</li>
            <li><strong>AI Processing:</strong> To process your assessment data through AI services (OpenAI/Gemini) to generate customized credit strategies and recommendations.</li>
            <li><strong>Account Management:</strong> To manage your account, process subscriptions, and provide customer support.</li>
            <li><strong>Analytics:</strong> To understand how users interact with our platform, identify trends, and improve our services.</li>
            <li><strong>Communication:</strong> To send you important updates about your account, subscription, or changes to our services.</li>
            <li><strong>Security:</strong> To detect, prevent, and address technical issues, fraud, or security threats.</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>4. Third-Party Services</h2>
          <p>We use the following third-party services that may collect or process your information:</p>
          
          <h3>4.1 Firebase (Google)</h3>
          <ul>
            <li><strong>Authentication:</strong> Firebase Authentication handles user login and account management.</li>
            <li><strong>Database:</strong> Firestore stores your assessment data, chat history, and user profile information.</li>
            <li><strong>Analytics:</strong> Firebase Analytics tracks usage patterns and user behavior.</li>
            <li>For more information, see <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer">Firebase Privacy Policy</a>.</li>
          </ul>

          <h3>4.2 Stripe</h3>
          <ul>
            <li><strong>Payment Processing:</strong> Stripe processes subscription payments and manages billing information.</li>
            <li>We do not store your full payment card details; this information is handled securely by Stripe.</li>
            <li>For more information, see <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">Stripe Privacy Policy</a>.</li>
          </ul>

          <h3>4.3 AI Service Providers</h3>
          <ul>
            <li><strong>OpenAI/Gemini:</strong> We may send your assessment data and queries to AI service providers to generate credit plans and recommendations.</li>
            <li>These services process your data according to their respective privacy policies.</li>
            <li>For more information, see <a href="https://openai.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer">OpenAI Privacy Policy</a> and <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a>.</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>5. Data Storage and Security</h2>
          <ul>
            <li><strong>Data Location:</strong> Your data is stored securely in Firebase's cloud infrastructure, which may be located in various geographic regions.</li>
            <li><strong>Security Measures:</strong> We implement industry-standard security measures, including encryption in transit and at rest, secure authentication, and access controls.</li>
            <li><strong>Data Access:</strong> Only you can access your own assessment data and chat history. We use Firebase security rules to ensure data isolation between users.</li>
            <li><strong>Data Retention:</strong> We retain your data for as long as your account is active or as needed to provide services. You can request deletion of your data at any time.</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>6. Your Rights and Choices</h2>
          <p>You have the following rights regarding your personal information:</p>
          <ul>
            <li><strong>Access:</strong> You can access and review your account information, assessment data, and chat history through your account dashboard.</li>
            <li><strong>Correction:</strong> You can update your account information and assessment data at any time.</li>
            <li><strong>Deletion:</strong> You can request deletion of your account and associated data by contacting us or using account deletion features.</li>
            <li><strong>Data Portability:</strong> You can export your assessment data and chat history from your account.</li>
            <li><strong>Opt-Out:</strong> You can opt out of certain analytics tracking, though this may affect some features of the service.</li>
            <li><strong>Subscription Management:</strong> You can manage or cancel your subscription at any time through the subscription management portal.</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>7. Cookies and Tracking Technologies</h2>
          <p>
            We use cookies and similar tracking technologies to track activity on our service and hold certain information. Cookies are files with a small amount of data that may include an anonymous unique identifier. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
          </p>
          <p>
            We use cookies for:
          </p>
          <ul>
            <li>Authentication and session management</li>
            <li>Analytics and usage tracking</li>
            <li>Improving user experience</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>8. Children's Privacy</h2>
          <p>
            Our service is not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you become aware that a child has provided us with personal information, please contact us, and we will take steps to delete such information.
          </p>
        </section>

        <section className="privacy-section">
          <h2>9. Data Sharing and Disclosure</h2>
          <p>We do not sell your personal information. We may share your information only in the following circumstances:</p>
          <ul>
            <li><strong>Service Providers:</strong> With third-party service providers (Firebase, Stripe, AI providers) who assist us in operating our platform and providing services.</li>
            <li><strong>Legal Requirements:</strong> When required by law, court order, or government regulation, or to protect our rights, property, or safety.</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity.</li>
            <li><strong>With Your Consent:</strong> We may share your information with your explicit consent for specific purposes.</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>10. International Data Transfers</h2>
          <p>
            Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that differ from those in your country. By using our service, you consent to the transfer of your information to these countries.
          </p>
        </section>

        <section className="privacy-section">
          <h2>11. Changes to This Privacy Policy</h2>
          <p>
            We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.
          </p>
        </section>

        <section className="privacy-section">
          <h2>12. Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy or wish to exercise your rights regarding your personal information, please contact us:
          </p>
          <ul>
            <li><strong>Email:</strong> <a href="mailto:support@stoiccredit.com">support@stoiccredit.com</a></li>

          </ul>
        </section>

        <section className="privacy-section">
          <h2>13. California Privacy Rights</h2>
          <p>
            If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA), including:
          </p>
          <ul>
            <li>The right to know what personal information we collect, use, and disclose</li>
            <li>The right to delete your personal information</li>
            <li>The right to opt-out of the sale of personal information (we do not sell personal information)</li>
            <li>The right to non-discrimination for exercising your privacy rights</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>14. GDPR Rights (European Users)</h2>
          <p>
            If you are located in the European Economic Area (EEA), you have additional rights under the General Data Protection Regulation (GDPR), including:
          </p>
          <ul>
            <li>The right to access your personal data</li>
            <li>The right to rectification of inaccurate data</li>
            <li>The right to erasure ("right to be forgotten")</li>
            <li>The right to restrict processing</li>
            <li>The right to data portability</li>
            <li>The right to object to processing</li>
            <li>Rights related to automated decision-making</li>
          </ul>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;

